import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/bio", label: "Bio" },
  { href: "/speaker", label: "Speaker" },
  { href: "/events", label: "Events" },
  { href: "/book", label: "Book" },
  { href: "/reviews", label: "Reviews" },
  { href: "/services", label: "Services" },
  { href: "/media", label: "Media Room" },
  { href: "/contact", label: "Contact" },
];

export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Disable body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        isScrolled
          ? "bg-background/95 backdrop-blur-md border-b border-border shadow-lg"
          : "bg-transparent"
      )}
    >
      <nav className="section-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="font-honey text-2xl sm:text-3xl text-gold hover:text-gold/80 transition-colors whitespace-nowrap"
          >
            Michele London
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  "px-2.5 xl:px-4 py-2 text-sm font-medium transition-all duration-200 rounded-md link-underline whitespace-nowrap",
                  location.pathname === link.href
                    ? "text-gold"
                    : "text-foreground/80 hover:text-foreground"
                )}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-foreground min-h-[44px] min-w-[44px] h-11 w-11"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </nav>

      {/* Mobile Navigation Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 w-screen h-screen flex flex-col bg-[hsl(222,40%,16%)]"
          style={{ zIndex: 9999 }}
        >
          {/* Header row matching the closed nav exactly: same section-container, same h-20, so the
              wordmark and the button stay put instead of the branding vanishing on open. */}
          <div className="section-container">
            <div className="flex items-center justify-between h-20">
              <Link
                to="/"
                className="font-honey text-2xl sm:text-3xl text-gold hover:text-gold/80 transition-colors whitespace-nowrap"
              >
                Michele London
              </Link>
              <button
                onClick={() => setIsOpen(false)}
                className="flex h-11 w-11 min-h-[44px] min-w-[44px] items-center justify-center"
                aria-label="Close menu"
              >
                <X className="h-7 w-7 text-white" strokeWidth={2.5} />
              </button>
            </div>
          </div>

          {/* Nav links sit just under the header row. Centring them in the leftover space put the
              first link ~88px below the header, and on a short phone the list is taller than that
              space, so centring clipped the first and last links with no way to scroll to them. */}
          <nav className="flex-1 overflow-y-auto flex flex-col items-center gap-1 px-4 pt-4 pb-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  "text-lg font-serif transition-all duration-200 py-2.5 px-6 rounded-lg min-h-[44px] flex items-center",
                  location.pathname === link.href
                    ? "text-gold bg-gold/10"
                    : "text-foreground/80 hover:text-gold hover:bg-gold/5"
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}